import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getShiftSwaps, createShiftSwap, acceptShiftSwap, rejectShiftSwap, cancelShiftSwap, getSchedules, getUsers } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeftRight, Plus, Check, X, Clock, User, Trash2 } from 'lucide-react';
import { format, parseISO, addDays } from 'date-fns';

export const ShiftSwapsPage = () => {
  const { user, hasRole } = useAuth();
  const [swaps, setSwaps] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');

  const [formData, setFormData] = useState({
    shift_id: '',
    target_user_id: '',
    reason: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = selectedTab !== 'all' ? { status: selectedTab } : {};
      const [swapsRes, shiftsRes] = await Promise.all([
        getShiftSwaps(params),
        getSchedules({ start_date: format(new Date(), 'yyyy-MM-dd'), end_date: format(addDays(new Date(), 30), 'yyyy-MM-dd') })
      ]);
      setSwaps(swapsRes.data);
      setMyShifts(shiftsRes.data.filter(s => s.user_id === user?.id));

      if (hasRole(['admin', 'manager', 'supervisor'])) {
        const usersRes = await getUsers();
        setUsers(usersRes.data.filter(u => u.id !== user?.id));
      }
    } catch (error) {
      console.error('Failed to fetch shift swaps:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTab, user?.id, hasRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateSwap = async (e) => {
    e.preventDefault();
    if (!formData.shift_id) {
      toast.error('Please select a shift');
      return;
    }
    try {
      const data = {
        shift_id: formData.shift_id,
        reason: formData.reason,
        target_user_id: formData.target_user_id || null
      };
      await createShiftSwap(data);
      toast.success('Swap request created');
      setDialogOpen(false);
      setFormData({ shift_id: '', target_user_id: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create swap request');
    }
  };

  const handleAccept = async (id) => {
    try {
      await acceptShiftSwap(id);
      toast.success('Swap accepted! Shift transferred to you.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept swap');
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectShiftSwap(id);
      toast.success('Swap request rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject swap');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this swap request?')) return;
    try {
      await cancelShiftSwap(id);
      toast.success('Swap request cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel swap');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="badge-success">Approved</Badge>;
      case 'rejected':
        return <Badge className="badge-error">Rejected</Badge>;
      case 'cancelled':
        return <Badge variant="outline">Cancelled</Badge>;
      default:
        return <Badge className="badge-warning">Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="swaps-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="shift-swaps-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
            Shift Swaps
          </h1>
          <p className="text-muted-foreground mt-1">
            Request and manage shift exchanges with colleagues
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="request-swap-btn">
              <Plus className="w-4 h-4 mr-2" />
              Request Swap
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Request Shift Swap</DialogTitle>
              <DialogDescription>
                Request to swap one of your shifts with a colleague
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSwap} className="space-y-4">
              <div className="space-y-2">
                <Label>Select Your Shift</Label>
                <Select
                  value={formData.shift_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, shift_id: value }))}
                >
                  <SelectTrigger data-testid="swap-shift-select">
                    <SelectValue placeholder="Choose a shift to swap" />
                  </SelectTrigger>
                  <SelectContent>
                    {myShifts.map((shift) => (
                      <SelectItem key={shift.id} value={shift.id}>
                        {shift.date} ({shift.start_time} - {shift.end_time})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {myShifts.length === 0 && (
                  <p className="text-sm text-muted-foreground">No upcoming shifts to swap</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Target Colleague (Optional)</Label>
                <Select
                  value={formData.target_user_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, target_user_id: value === 'open' ? '' : value }))}
                >
                  <SelectTrigger data-testid="swap-target-select">
                    <SelectValue placeholder="Anyone (open request)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Anyone (open request)</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Leave empty for an open swap request any colleague can accept
                </p>
              </div>

              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea
                  placeholder="Why do you need to swap this shift?"
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  data-testid="swap-reason"
                />
              </div>

              <Button type="submit" className="w-full" disabled={myShifts.length === 0} data-testid="submit-swap-btn">
                Submit Swap Request
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Swaps List */}
      <Card>
        <CardContent className="p-0">
          {swaps.length === 0 ? (
            <div className="empty-state py-12">
              <ArrowLeftRight className="empty-state-icon" />
              <p className="text-muted-foreground">No swap requests found</p>
            </div>
          ) : (
            <div className="divide-y">
              {swaps.map((swap) => (
                <div
                  key={swap.id}
                  className="p-6 hover:bg-muted/50 transition-colors"
                  data-testid={`swap-${swap.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <ArrowLeftRight className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{swap.requester_name}</span>
                          {swap.target_name ? (
                            <>
                              <span className="text-muted-foreground">→</span>
                              <span className="font-medium">{swap.target_name}</span>
                            </>
                          ) : (
                            <Badge variant="outline" className="text-xs">Open Request</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <Clock className="w-3 h-3" />
                          <span>{swap.shift_date} ({swap.shift_start} - {swap.shift_end})</span>
                        </div>
                        {swap.reason && (
                          <p className="text-sm text-muted-foreground mt-2 italic">
                            "{swap.reason}"
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {getStatusBadge(swap.status)}
                      
                      {swap.status === 'pending' && swap.requester_id !== user?.id && (
                        (!swap.target_user_id || swap.target_user_id === user?.id) && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 hover:text-emerald-700"
                              onClick={() => handleAccept(swap.id)}
                              data-testid={`accept-swap-${swap.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-rose-600 hover:text-rose-700"
                              onClick={() => handleReject(swap.id)}
                              data-testid={`reject-swap-${swap.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )
                      )}
                      
                      {swap.status === 'pending' && swap.requester_id === user?.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => handleCancel(swap.id)}
                          data-testid={`cancel-swap-${swap.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
